import * as Sentry from '@sentry/react'
import { kPaymentsSetupIntentPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

export type CreateStripeSetupIntentResponse = {
  client_secret?: string
  setup_intent_id?: string
  error?: boolean
  errorDetail?: string
}

export default async function createStripeSetupIntent(): Promise<CreateStripeSetupIntentResponse> {
  // Create transaction id
  const sentryTransactionId = nanoid()
  const apiUrl = kPaxtonAppApiRESTBaseUrl() + `/${kPaymentsSetupIntentPath}`

  try {
    const token = await getAuth().currentUser?.getIdToken(true)
    if (!token) throw Error(kErrorCodeAuthError)
    // Construct request
    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
    }

    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()
    if (!response.ok || !data) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })

        Sentry.captureException(new Error(`createStripeSetupIntent: !response.ok || !data, status: ${response.status}`), {
          extra: {
            response: data,
            data: JSON.stringify(data),
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new Error(`Fetch error - status: ${response.status}`) // maintain previous throwing behavior for ux
    }

    if (data.success == false || data.error != null || data.error != undefined) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })

        Sentry.captureException(
          new Error(`createStripeSetupIntent: data.success == false || data.error != null || data.error != undefined, status: ${response.status}`),
          {
            extra: {
              response: data,
              data: JSON.stringify(data),
              requestUrl: apiUrl,
              requestOptions: requestOptions,
              onLine: navigator.onLine,
              cookieEnabled: navigator.cookieEnabled,
            },
          }
        )
      })
      throw new Error(`Fetch error - status: ${kPaymentsSetupIntentPath}, detail: ${data.error}`) // maintain previous throwing behavior for ux
    }

    return data
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })

      Sentry.captureException(new Error(`createStripeSetupIntent: fetch error`)),
        {
          extra: {
            error: JSON.stringify(e),
            requestUrl: apiUrl,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        }
    })

    // rethrow the error
    throw e // maintain previous throwing behavior for ux
  }
}
